import classNames from "classnames";
import { valHooks } from "jquery";
import { DateTime } from "luxon";
import { useState, useContext } from "react";
import { common_utils } from "../../../../../../common/common_utils";
import { Constants } from "../../../../../../common/constants";
import { ServerReminderDescriptor } from "../../../../../../src/entity/ServerReminder";
import { DashboardUtils } from "../../../dashboard_utils";
import { EventCreationContext } from "../../../event_creation";
import { parseEmbedTitle } from "../../../markdown_parser/markdown";
import { NumberInputForm } from "../../number_input";
import { TextAreaForm } from "../../text_area";
import { AutocompleteInput } from "../autocomplete_input/autocomplete_input";
import { ModifySettingsButton } from "../base_components/modify_settings_button";
import { SeshAddonInput } from "../base_components/sesh_addon_input";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { SeshSelect } from "../base_components/sesh_select_input";
import { SeshTextInput } from "../base_components/sesh_text_input";
import ChannelPicker from "../channel_picker";
import { Disabler } from "../disabler";
import { DiscordEmbedComponent } from "../discord_embed/discord_embed";
import { ErrorNote } from "../error_note";
import { useChannels } from "../hooks/use_channels";
import { SelectableBox } from "../selectable_box";
import { SettingsItem } from "../settings_item";
import { SimpleSelectableBoxItem } from "../simple_selectable_box_item";
import {
	getReminderDiscretizedTime,
	reminderDiscretizedTimeToMs,
	ReminderTimeUnit,
	ReminderTimeUnitDescriptors,
} from "./server_reminders_blurb";
import { MentionsSection } from "./server_reminders_mentions";

type ModalReminder = Omit<ServerReminderDescriptor, "fire_before_ms"> & {
	time: {
		count: number;
		unit: keyof typeof ReminderTimeUnitDescriptors;
	};
};

type ModalProps = {
	is_edit: boolean;
	value: ServerReminderDescriptor;
	event_channel_id?: string;
	other_reminders: ServerReminderDescriptor[];
	onClose: () => void;
	addReminder: (reminder: ServerReminderDescriptor) => void;
};
export function ServerRemindersModal(props: ModalProps) {
	return (
		<DashboardUtils.SeshModal
			is_active={true}
			onClose={props.onClose}
			title={"🔔 Server Notification"}
			content={<ModalContent {...props} />}
		/>
	);
}

function ContentChoice(props: {
	setChoice: (choice: "Start" | "Pre") => void;
}) {
	return (
		<div className="flex flex-col gap-2">
			<p className="font-bold">Select notification time</p>
			<SimpleSelectableBoxItem
				header={"Before Event Start"}
				description={
					"Create a notification that will be sent before the event starts"
				}
				icon={<i className="fas fa-hourglass-start"></i>}
				onClick={() => props.setChoice("Pre")}
				bgColor="semidark"
			/>
			<SimpleSelectableBoxItem
				header={"At Event Start"}
				description={
					"Create a notification that will be sent when the event starts"
				}
				icon={<i className="fas fa-hourglass-end"></i>}
				onClick={() => props.setChoice("Start")}
				bgColor="semidark"
			/>
		</div>
	);
}

function ModalContent(props: ModalProps) {
	const [reminder, setReminder] = useState<ModalReminder>({
		...props.value,
		time:
			props.value.fire_before_ms !== undefined
				? getReminderDiscretizedTime(props.value)
				: undefined,
	});

	const [is_editing_mentions, setIsEditingMentions] = useState(false);

	// It is a new reminder and choice has not been set yet
	if (reminder.time === undefined) {
		return (
			<ContentChoice
				setChoice={(choice) => {
					setReminder({
						...reminder,
						time: {
							count: choice === "Pre" ? 10 : 0,
							unit: "minutes",
						},
					});
				}}
			/>
		);
	}

	const { is_valid, error_message } = common_utils.ValidateReminder(
		{
			...reminder,
			fire_before_ms: reminderDiscretizedTimeToMs(reminder.time),
		},
		props.other_reminders,
		props.event_channel_id
	);

	return (
		<div className="mt-2 flex flex-col gap-4">
			<SettingsItem active>
				<TimeSection
					onChange={(val) => setReminder({ ...reminder, time: val })}
					value={reminder.time}
				/>
			</SettingsItem>
			<SettingsItem active>
				<ChannelSelect
					channel_id={reminder.desired_channel_id}
					setChannelId={(val) =>
						setReminder({
							...reminder,
							desired_channel_id: val,
						})
					}
				/>
			</SettingsItem>
			<SettingsItem active>
				<MessageSection
					value={{
						message_description: reminder.message_description,
						message_title: reminder.message_title,
					}}
					onChange={(val) => {
						setReminder({
							...reminder,
							...val,
						});
					}}
					fire_time_ms={reminderDiscretizedTimeToMs(reminder.time)}
				/>
			</SettingsItem>
			<SettingsItem active>
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-2">
						<div className="font-bold">Mentions</div>
						<ModifySettingsButton
							hide_arrow
							className="ml-auto"
							onClick={() =>
								setIsEditingMentions(!is_editing_mentions)
							}
						>
							<div>
								{is_editing_mentions ? "preview" : "edit"}
							</div>
						</ModifySettingsButton>
					</div>
					<MentionsSection
						member_ids={reminder.user_mention_ids}
						role_ids={reminder.role_mention_ids}
						is_editing={is_editing_mentions}
						onMembersChange={(val) =>
							setReminder({
								...reminder,
								user_mention_ids: val,
							})
						}
						onRolesChange={(val) =>
							setReminder({
								...reminder,
								role_mention_ids: val,
							})
						}
					/>
				</div>
			</SettingsItem>
			<div className="flex items-center gap-2">
				{error_message && (
					<div className="flex-grow text-sm">
						<ErrorNote error={error_message} />
					</div>
				)}
				<div className="ml-auto flex w-fit">
					<SeshPrimaryButton
						disabled={!is_valid}
						className="ml-auto"
						onClick={() =>
							props.addReminder({
								...reminder,
								fire_before_ms: reminderDiscretizedTimeToMs(
									reminder.time
								),
							})
						}
					>
						{props.is_edit ? "Edit" : "Add"}
					</SeshPrimaryButton>
				</div>
			</div>
		</div>
	);
}

function TimeSection(props: {
	value: ModalReminder["time"];
	onChange: (val: ModalReminder["time"]) => void;
}) {
	return (
		<div className="flex flex-col gap-2">
			<div className="font-bold">Fire Time</div>

			{props.value.count === 0 && (
				<div
					className="flex cursor-pointer items-center gap-2 text-sesh-primary hover:text-sesh-dark"
					onClick={() => props.onChange(undefined)}
				>
					<div className="font-bold text-sesh-primary">
						Event Start
					</div>
					<i className="fas fa-pencil-alt" />
				</div>
			)}
			{props.value.count > 0 && (
				<div className="limit-form flex flex-wrap items-center gap-2">
					<div className="flex items-center gap-2">
						<NumberInputForm
							name="limit"
							allow_zero
							initial_value={props.value.count}
							do_not_update_initially={true}
							updateFunc={(val) => {
								props.onChange({
									...props.value,
									count: val,
								});
							}}
						/>
						<div className="flex-shrink-0">
							<SeshSelect
								value={{
									label: props.value.unit,
									value: props.value.unit,
								}}
								options={[
									{
										label: "minutes",
										value: "minutes",
									},
									{
										label: "hours",
										value: "hours",
									},
									{
										label: "days",
										value: "days",
									},
									{
										label: "weeks",
										value: "weeks",
									},
								]}
								onChange={(val) =>
									props.onChange({
										...props.value,
										unit: val.value as ReminderTimeUnit,
									})
								}
							/>
						</div>
					</div>
					<div>before event start</div>
				</div>
			)}
		</div>
	);
}

function ChannelSelect(props: {
	channel_id?: string;
	setChannelId: (channel_id: string) => void;
}) {
	const [channel_type, setChannelType] = useState<"event" | "custom">(
		props.channel_id ? "custom" : "event"
	);

	const channels = useChannels();

	return (
		<div className="flex flex-col gap-2">
			<div className="font-bold">Channel</div>
			<SeshAddonInput
				value={channel_type}
				setValue={(val) => {
					setChannelType(val as typeof channel_type);
					if (val === "custom") {
						props.setChannelId(channels[0].id);
					} else {
						props.setChannelId(undefined);
					}
				}}
				buttons={[
					{
						label: "Event's Channel",
						value: "event",
					},
					{
						label: "Custom Channel",
						value: "custom",
					},
				]}
			></SeshAddonInput>
			{channel_type === "custom" && (
				<ChannelPicker
					channel_id={props.channel_id}
					setChannel={(val) => props.setChannelId(val)}
				/>
			)}
		</div>
	);
}

function MessageSection(props: {
	fire_time_ms: number;
	value: Pick<
		ServerReminderDescriptor,
		"message_description" | "message_title"
	>;
	onChange: (
		val: Pick<
			ServerReminderDescriptor,
			"message_description" | "message_title"
		>
	) => void;
}) {
	const [is_editing, setIsEditing] = useState(false);

	const event_creation_context = useContext(EventCreationContext);

	return (
		<div className="flex flex-col">
			<div className="flex flex-col gap-2">
				<div className="flex items-center gap-2 font-bold">
					<div>Message</div>
					<ModifySettingsButton
						className="ml-auto"
						hide_arrow
						onClick={() => setIsEditing(!is_editing)}
					>
						{is_editing ? "preview" : "edit"}
					</ModifySettingsButton>
				</div>
				{is_editing ? (
					<>
						<div className="flex flex-col gap-1">
							{is_editing && (
								<span className="text-xs">
									Type{" "}
									<span className="command-example--inline">
										$
									</span>{" "}
									for a full list of{" "}
									<a
										className="manual-link"
										href={
											DashboardUtils.GetManualLink() +
											`/placeholders`
										}
										target="_blank"
									>
										placeholders
									</a>
									.
								</span>
							)}
							<TitleText
								fire_time_ms={props.fire_time_ms}
								value={props.value.message_title}
								setValue={(str) =>
									props.onChange({
										...props.value,
										message_title: str,
									})
								}
							/>
						</div>
						<DescriptionText
							fire_time_ms={props.fire_time_ms}
							value={props.value.message_description}
							setValue={(str) => {
								props.onChange({
									...props.value,
									message_description: str,
								});
							}}
						/>
					</>
				) : (
					<DiscordEmbedComponent
						title={replacePlaceholders(
							"EmbedTitle",
							event_creation_context,
							props.fire_time_ms,
							props.value.message_title
						)}
						description={replacePlaceholders(
							"EmbedDescription",
							event_creation_context,
							props.fire_time_ms,
							props.value.message_description
						)}
						color={event_creation_context?.state.color}
					/>
				)}
			</div>
		</div>
	);
}

type LocationContextObject = typeof EventCreationContext extends React.Context<
	infer U
>
	? U
	: never;

function replacePlaceholders(
	type: common_utils.PlaceholderType,
	context: LocationContextObject,
	fire_time_ms: number,
	value: string
) {
	return common_utils.replacePlaceholders(type, value, {
		event_link: undefined,
		event_name: context?.state.event_name,
		start_time: context?.state.start_time,
		timezone: context?.props.guild_timezone,
		duration_ms: fire_time_ms,
		description: context?.state.description,
		rsvp_options: context?.state.rsvp_options.custom_options,
	});
}

function DescriptionText(props: {
	fire_time_ms: number;
	value: string;
	setValue: (str: string) => void;
}) {
	return (
		<AutocompleteInput
			placeholder="Description"
			value={props.value}
			onChange={(str) => props.setValue(str)}
			char_limit={Constants.DESCRIPTION_CHAR_LIMIT - 400}
			type="description"
		></AutocompleteInput>
	);
}

function TitleText(props: {
	fire_time_ms: number;
	value: string;
	setValue: (str: string) => void;
}) {
	return (
		<AutocompleteInput
			placeholder="Title"
			onChange={(str) => props.setValue(str)}
			type="title"
			value={props.value}
			char_limit={Constants.EMBED_TITLE_LIMIT}
		/>
	);
}
